import { ouchnCustom } from "@/service";
import { baseURL } from "./config";

/**
 * 业务参数默认值。
 * bookId / chapterId / flowId 可通过访问地址路由 query 覆盖，见 resolveGuidedLearningConfig。
 * AK/SK 与上游签名改由 ouchn-custom-api 中转完成。
 */
export const GUIDED_LEARNING_CONFIG = {
  bookId: "019f3a1f-86f9-7e5e-9a98-e8e317012ffa",
  chapterId: "4fa9d670-9dc7-4cd0-b082-fb03d9d5e696",
  flowId: "c3d06876-6e9c-46ce-8d6d-4da786021b11",
  userId: "1111",
  systemPrompt: "",
  source: process.env.VUE_APP_GUIDED_LEARNING_SOURCE || "CloudAcademy-test",
};

/**
 * 从路由 query 解析业务配置。
 * bookId、chapterId、flowId：路由有值则覆盖，否则用默认值。
 * 示例：/intelligentAnswering_v2?bookId=xxx&chapterId=yyy&flowId=zzz
 */
export function resolveGuidedLearningConfig(routeQuery = {}) {
  const query = routeQuery || {};
  return {
    ...GUIDED_LEARNING_CONFIG,
    bookId: query.bookId || GUIDED_LEARNING_CONFIG.bookId,
    chapterId: query.chapterId || GUIDED_LEARNING_CONFIG.chapterId,
    flowId: query.flowId || GUIDED_LEARNING_CONFIG.flowId,
  };
}

function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function createGuidedLearningId() {
  return uuid();
}

function getProxyBase() {
  return `${baseURL}${ouchnCustom}/web/bookAi/guidedLearning`;
}

function buildQueryString(parameters = {}) {
  const search = new URLSearchParams();
  Object.keys(parameters || {}).forEach((key) => {
    const value = parameters[key];
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function authHeaders(extra = {}) {
  return {
    platform: "custom",
    ...extra,
  };
}

async function parseJsonResponse(response) {
  const text = await response.text();
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch (e) {
    throw new Error(text || `请求失败: ${response.status}`);
  }
  if (!response.ok) {
    throw new Error((json && (json.message || json.msg)) || `请求失败: ${response.status}`);
  }
  if (json && json.state === false) {
    throw new Error(json.message || json.msg || "接口返回失败");
  }
  return json;
}

async function requestJson({ path, parameters = {} }) {
  const url = `${getProxyBase()}${path}${buildQueryString(parameters)}`;
  if (process.env.NODE_ENV === "development") {
    console.info("[guidedLearning]", "GET", url, parameters);
  }
  const response = await fetch(url, {
    method: "GET",
    headers: authHeaders(),
  });
  return parseJsonResponse(response);
}

function extractJsonChunks(text) {
  const chunks = [];
  let start = -1;
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === "\\") {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === "{") {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0 && start >= 0) {
        chunks.push(text.slice(start, i + 1));
        start = -1;
      }
    }
  }

  if (!chunks.length && text.trim()) {
    chunks.push(text.trim());
  }
  return chunks;
}

function getStreamField(json, lowerKey, upperKey) {
  return json[lowerKey] !== undefined ? json[lowerKey] : json[upperKey];
}

function normalizeFinalAiMessage(content, state) {
  if (!content) return state.content;
  if (!state.content) return content;
  if (content === state.content) return state.content;
  if (content.includes(state.content)) return content;
  if (state.content.includes(content)) return state.content;
  return content;
}

function logStreamDebug(msgType, content, state) {
  if (process.env.NODE_ENV !== "development") return;
  console.info("[guidedLearning] stream chunk", {
    msgType,
    contentLength: content.length,
    hasContent: !!content,
    receivedContent: state.receivedContent,
    loadingCompleted: state.loadingCompleted,
  });
}

function applyStreamJson(json, state) {
  const chunkContent = String(getStreamField(json, "content", "Content") || "");
  const msgType = String(getStreamField(json, "msgType", "MsgType") || "").toLowerCase();
  const sessionId = getStreamField(json, "sessionId", "SessionId");
  const msgId = getStreamField(json, "msgId", "MsgId");

  if (sessionId) state.sessionId = sessionId;
  if (msgId) state.msgId = msgId;

  logStreamDebug(msgType, chunkContent, state);

  if (msgType === "error") {
    state.error = chunkContent || json.message || json.Message || "流式接口返回错误";
    state.loadingCompleted = true;
    return;
  }

  if (!chunkContent) {
    return;
  }

  state.receivedContent = true;
  if (msgType === "chunk") {
    state.content += chunkContent;
    return;
  }

  if (msgType === "aimsg") {
    state.content = normalizeFinalAiMessage(chunkContent, state);
    state.loadingCompleted = true;
    return;
  }

  state.content += chunkContent;
}

function consumeSseLine(line, state) {
  let data = (line || "").trim();
  if (!data) return;
  if (data.startsWith("data:")) {
    data = data.substring(5).trim();
  }
  if (!data || data === "[DONE]") {
    state.loadingCompleted = true;
    return;
  }

  extractJsonChunks(data).forEach((chunk) => {
    try {
      applyStreamJson(JSON.parse(chunk), state);
    } catch (e) {
      // 上游有时返回非 SSE 格式片段，等待后续分片补齐
    }
  });
}

/**
 * 1. 流式通用对话（经 custom-api 中转）
 * action: load | answer | next
 */
export async function streamGuidedLearningChat({
  action,
  content = "",
  sessionId = "",
  questionAttemptId,
  nextQuestionAttemptId = "",
  signal,
  onUpdate,
  config = {},
}) {
  const merged = { ...GUIDED_LEARNING_CONFIG, ...config };
  const payload = {
    inputMsg: {
      msgId: uuid(),
      senderType: "user",
      senderId: merged.userId || "user",
      msgType: "text",
      content: content || "",
    },
    bookId: merged.bookId,
    userId: merged.userId,
    systemPrompt: merged.systemPrompt || null,
    parameters: {
      chapterId: merged.chapterId,
      questionAttemptId,
      action,
    },
  };
  if (config.flowId) {
    payload.flowId = config.flowId;
  }
  if (config.source) {
    payload.source = config.source;
  }
  if (sessionId) {
    payload.sessionId = sessionId;
  }
  if (action === "next" && nextQuestionAttemptId) {
    payload.parameters.nextQuestionAttemptId = nextQuestionAttemptId;
  }

  const url = `${getProxyBase()}/chatStream`;
  if (process.env.NODE_ENV === "development") {
    console.info("[guidedLearning] POST", url, payload);
  }

  const response = await fetch(url, {
    method: "POST",
    headers: authHeaders({
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    }),
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    let errorMsg = `流式对话请求失败: ${response.status}`;
    try {
      const errorText = await response.text();
      if (errorText) {
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.message || errorJson.msg || errorText;
        } catch (e) {
          errorMsg = errorText;
        }
      }
    } catch (e) {
      // ignore
    }
    throw new Error(errorMsg);
  }
  if (!response.body) {
    throw new Error("流式对话响应体为空");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let lineBuffer = "";
  const state = {
    content: "",
    loadingCompleted: false,
    sessionId: sessionId || "",
    msgId: "",
    error: "",
    receivedContent: false,
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split(/\r?\n/);
    lineBuffer = lines.pop() || "";
    lines.forEach((line) => consumeSseLine(line, state));
    if (!lineBuffer.includes("data:") && !lineBuffer.includes("\n")) {
      const chunks = extractJsonChunks(lineBuffer);
      if (chunks.length) {
        chunks.forEach((chunk) => consumeSseLine(chunk, state));
        const lastChunk = chunks[chunks.length - 1];
        const lastIndex = lineBuffer.lastIndexOf(lastChunk);
        lineBuffer = lastIndex >= 0 ? lineBuffer.slice(lastIndex + lastChunk.length) : "";
      }
    }
    if (onUpdate) {
      onUpdate({
        content: state.content,
        loadingCompleted: state.loadingCompleted,
        sessionId: state.sessionId,
        error: state.error,
      });
    }
  }

  const remaining = lineBuffer.trim();
  if (remaining) {
    if (remaining.includes("data:")) {
      remaining.split(/\r?\n/).forEach((line) => consumeSseLine(line, state));
    } else {
      extractJsonChunks(remaining).forEach((chunk) => consumeSseLine(chunk, state));
    }
  }
  state.loadingCompleted = true;
  if (state.error) {
    throw new Error(state.error);
  }
  if (onUpdate) {
    onUpdate({
      content: state.content,
      loadingCompleted: true,
      sessionId: state.sessionId,
      error: state.error,
    });
  }
  return {
    content: state.content,
    loadingCompleted: true,
    sessionId: state.sessionId,
  };
}

export async function fetchGuidedLearningChapters(params = {}) {
  const merged = { ...GUIDED_LEARNING_CONFIG, ...params };
  return requestJson({
    path: "/chapters",
    parameters: {
      bookId: merged.bookId,
      chapterId: merged.chapterId,
      pageNum: merged.pageNum || 1,
      pageSize: merged.pageSize || 20,
    },
  });
}

export async function fetchGuidedLearningSessions(params = {}) {
  const merged = { ...GUIDED_LEARNING_CONFIG, ...params };
  return requestJson({
    path: "/sessions",
    parameters: {
      bookId: merged.bookId,
      userId: merged.userId,
      flowId: merged.flowId,
      pageNum: merged.pageNum || 1,
      pageSize: merged.pageSize || 20,
    },
  });
}

export async function fetchGuidedLearningMessages(params = {}) {
  return requestJson({
    path: "/messages",
    parameters: {
      sessionId: params.sessionId,
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 100,
    },
  });
}
